import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronRight, Inbox, Loader2, Pencil, Plus, RefreshCw, Trash2, XCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CursorPager, type Cursors } from "@/components/ui/cursor-pager";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AssignCompanyBillDialog } from "@/components/companies/AssignCompanyBillDialog";
import { CompanyForwardingSection } from "@/components/companies/CompanyForwardingSection";
import { CompanyInboundEmailsSection } from "@/components/companies/CompanyInboundEmailsSection";
import { EditCompanyDialog } from "@/components/companies/EditCompanyDialog";
import { LinkOrganisationDialog } from "@/components/companies/LinkOrganisationDialog";
import { DeliveryAddressBlock } from "@/components/companies/DeliveryAddressBlock";
import { ViewCompanyBillDocumentButton } from "@/components/companies/ViewCompanyBillDocumentButton";
import { InfoHint } from "@dolfin/ui";
import { api } from "@/api/client";
import { toastApiError, toastApiSuccess } from "@/lib/api-toast";
import type { components } from "@dolfin/api-admin";

type CompanyResponse = components["schemas"]["CompanyResponse"];
type CompanyInboxBillResponse = components["schemas"]["CompanyInboxBillResponse"];
type OrganisationResponse = components["schemas"]["OrganisationResponse"];
type CompanyRoutingOutcome = components["schemas"]["CompanyRoutingOutcome"];

const dateFmt = new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", year: "numeric" });
const scoreFmt = new Intl.NumberFormat("en-GB", { style: "percent", maximumFractionDigits: 0 });

/** A candidate's score source in words: the raw signal name ("postcode") says nothing about its weight. */
const BASIS_LABEL: Record<string, string> = {
  postcode: "an exact postcode match",
  address: "overlap in the address text",
  name: "the site's name appearing in the delivery block",
};

/** Why a bill is in the inbox, worded as what the reader must decide, not what the matcher computed. */
const OUTCOME_LABEL: Record<string, { label: string; hint: string }> = {
  BelowThreshold: {
    label: "No confident match",
    hint: "The delivery address did not match any of this company's organisations closely enough.",
  },
  Ambiguous: {
    label: "Too close to call",
    hint: "Two or more organisations matched about equally well, so it needs a person to decide.",
  },
  HeadOffice: {
    label: "Delivered to head office",
    hint: "This looks like a group cost rather than one site's, so it was not assigned automatically.",
  },
  MultipleDeliveryAddresses: {
    label: "Covers several sites",
    hint: "The bill names more than one delivery address. A bill can only be assigned to one organisation.",
  },
  NoDeliveryAddress: {
    label: "No delivery address",
    hint: "No delivery address could be read from the document, so there was nothing to match on.",
  },
  NoOrganisations: {
    label: "No organisations",
    hint: "This company has no organisations to route to yet.",
  },
  AutoAssignDisabled: {
    label: "Automatic routing off",
    hint: "Automatic routing is switched off, so every bill waits here.",
  },
};

// Decimals are `number | string` in the spec, so coerce before formatting.
function money(amount?: number | string | null, currency?: string): string {
  if (amount === undefined || amount === null) return "Not set";
  const n = typeof amount === "number" ? amount : Number(amount);
  if (Number.isNaN(n)) return "Not set";
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: currency || "GBP" }).format(n);
}

// Same `number | string` coercion as money(), for the 0-1 match scores.
function pct(value?: number | string | null): string | null {
  if (value === undefined || value === null) return null;
  const n = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(n)) return null;
  return scoreFmt.format(n);
}

const PAGE_SIZE = 25;

export function CompanyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [company, setCompany] = useState<CompanyResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [assigning, setAssigning] = useState<CompanyInboxBillResponse | null>(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [busyBillId, setBusyBillId] = useState<string | null>(null);

  // Cursor-paginated, not capped: rows come newest-first, so a cap would hide the oldest waiting bills.
  const [organisations, setOrganisations] = useState<OrganisationResponse[] | null>(null);
  const [orgCursors, setOrgCursors] = useState<Cursors | null>(null);
  const [orgCursor, setOrgCursor] = useState<string | null>(null);
  const [orgError, setOrgError] = useState<string | null>(null);

  const [bills, setBills] = useState<CompanyInboxBillResponse[] | null>(null);
  const [billCursors, setBillCursors] = useState<Cursors | null>(null);
  const [billCursor, setBillCursor] = useState<string | null>(null);
  const [billError, setBillError] = useState<string | null>(null);

  // Confirmations: neither action is undoable here, and unlinking changes where future bills can route.
  const [discarding, setDiscarding] = useState<CompanyInboxBillResponse | null>(null);
  const [unlinking, setUnlinking] = useState<OrganisationResponse | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  // Each load claims a ticket and writes only if still the latest, so a stale page reply can't overwrite a newer one.
  const orgRequest = useRef(0);
  const billRequest = useRef(0);

  const loadOrganisations = useCallback(async (companyId: string, cursor: string | null) => {
    const ticket = ++orgRequest.current;
    const { data, error } = await api.GET("/v1/companies/{companyId}/organisations", {
      params: { path: { companyId }, query: { Limit: PAGE_SIZE, ...(cursor ? { Cursor: cursor } : {}) } },
    });
    if (ticket !== orgRequest.current) return;

    // A failed request isn't "no sites"; don't render both as an empty table.
    if (error) {
      setOrgError("Couldn't load this company's organisations.");
      setOrganisations([]);
      return;
    }
    setOrgError(null);
    setOrganisations(data?.data ?? []);
    setOrgCursors(data?.meta?.cursors ?? null);
  }, []);

  const loadBills = useCallback(async (companyId: string, cursor: string | null) => {
    const ticket = ++billRequest.current;
    const { data, error } = await api.GET("/v1/companies/{companyId}/bills", {
      params: { path: { companyId }, query: { Limit: PAGE_SIZE, ...(cursor ? { Cursor: cursor } : {}) } },
    });
    if (ticket !== billRequest.current) return;

    if (error) {
      setBillError("Couldn't load the bills waiting for this company.");
      setBills([]);
      return;
    }
    setBillError(null);
    setBills(data?.data ?? []);
    setBillCursors(data?.meta?.cursors ?? null);
  }, []);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      const { data, error } = await api.GET("/v1/companies/{companyId}", {
        params: { path: { companyId: id } },
      });
      if (cancelled) return;
      if (error || !data) {
        setError("Couldn't load this company.");
        setLoading(false);
        return;
      }
      setCompany(data);
      if (!cancelled) setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    if (id) void loadOrganisations(id, orgCursor);
  }, [id, orgCursor, loadOrganisations]);

  useEffect(() => {
    if (id) void loadBills(id, billCursor);
  }, [id, billCursor, loadBills]);

  async function handleUnlink(organisationId: string) {
    const { error, response } = await api.DELETE("/v1/organisations/{organisationId}/company", {
      params: { path: { organisationId } },
    });
    setUnlinking(null);
    if (error) {
      await toastApiError({ error, response }, "Couldn't remove the organisation.");
      return;
    }
    toastApiSuccess("Organisation removed from company");
    if (id) await loadOrganisations(id, orgCursor);
  }

  async function handleDiscard(billId: string) {
    if (!id) return;
    setBusyBillId(billId);
    try {
      const { error, response } = await api.POST("/v1/companies/{companyId}/bills/{billId}/discard", {
        params: { path: { companyId: id, billId } },
        body: {},
      });
      setDiscarding(null);
      if (error) {
        await toastApiError({ error, response }, "Couldn't discard the bill.");
        return;
      }
      toastApiSuccess("Bill discarded", "It is kept as a record but will not reach any ledger.");
      await loadBills(id, billCursor);
    } finally {
      setBusyBillId(null);
    }
  }

  async function handleRetry(billId: string) {
    if (!id) return;
    setBusyBillId(billId);
    try {
      const { error, response } = await api.POST(
        "/v1/companies/{companyId}/bills/{billId}/retry-extraction",
        { params: { path: { companyId: id, billId } } },
      );
      if (error) {
        await toastApiError({ error, response }, "Couldn't retry reading the document.");
        return;
      }
      toastApiSuccess("Reading the document again");
      await loadBills(id, billCursor);
    } finally {
      setBusyBillId(null);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-24 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Loading company...
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-8">
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {error ?? "Company not found."}
        </div>
      </div>
    );
  }

  const waiting = (bills ?? []).filter((b) => b.status === "AwaitingOrganisation");
  const failed = (bills ?? []).filter((b) => b.status === "ExtractionFailed");
  const extracting = (bills ?? []).filter((b) => b.status === "Extracting");

  const headOffice = [company.addressLine1, company.addressLine2, company.city, company.postalCode, company.country]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-8">
      <div className="flex items-center gap-1 text-sm text-muted-foreground">
        <Link to="/companies" className="hover:text-foreground">
          Companies
        </Link>
        <ChevronRight className="size-3.5" />
        <span className="text-foreground">{company.name}</span>
      </div>

      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-3xl font-bold">{company.name}</h1>
          <p className="mt-1 font-mono text-xs text-muted-foreground">{company.id}</p>
          {/* Head office matters: routing suppresses a bill delivered here rather than assigning it by postcode. */}
          <p className="mt-2 text-sm text-muted-foreground">
            {headOffice ? (
              <>
                <span className="uppercase tracking-wider text-xs">Head office</span> {headOffice}
              </>
            ) : (
              <span className="italic">
                No head office address — bills addressed to the group cannot be recognised as group
                costs.
              </span>
            )}
          </p>
        </div>
        <Button variant="outline" size="sm" className="shrink-0" onClick={() => setEditOpen(true)}>
          <Pencil className="size-3.5" /> Edit
        </Button>
      </div>

      {id && <CompanyForwardingSection companyId={id} />}

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Inbox className="size-4" /> Bills waiting to be assigned
            </CardTitle>
            <CardDescription>
              These could not be matched to one organisation confidently enough to assign
              automatically. Assigning one makes it that organisation's bill and starts its normal
              review.
            </CardDescription>
          </div>
          {/* Only when everything fits on one page: across pages the count would mean nothing. */}
          {!billCursors?.next && !billCursors?.previous && (
            <Badge variant="outline" className="shrink-0">
              {waiting.length} waiting
            </Badge>
          )}
        </CardHeader>
        <CardContent>
          {billError ? (
            <p className="py-6 text-center text-sm text-destructive">{billError}</p>
          ) : bills === null ? (
            // Not the empty state: "Nothing waiting" can't be claimed before the request lands.
            <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
              <Spinner className="size-3.5" /> Loading bills...
            </div>
          ) : waiting.length === 0 && extracting.length === 0 && failed.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Nothing waiting. Bills that match a single organisation are assigned automatically and
              never appear here.
            </p>
          ) : (
            <div className="overflow-hidden rounded-md border border-border">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/40">
                    <TableHead>Supplier</TableHead>
                    <TableHead>Delivered to</TableHead>
                    <TableHead>Why it is waiting</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Received</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[...waiting, ...extracting, ...failed].map((b) => (
                    <TableRow key={b.id}>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium">{b.supplierName ?? "Unknown supplier"}</span>
                          <span className="text-xs text-muted-foreground">
                            {b.invoiceNumber ?? "No invoice number"}
                          </span>
                          {b.billPoNumbers && b.billPoNumbers.length > 0 && (
                            <span className="text-xs text-muted-foreground">
                              Extracted PO: {b.billPoNumbers.join(", ")}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[15rem] align-top">
                        <DeliveryAddressBlock bill={b} />
                      </TableCell>
                      <TableCell>
                        <StatusCell bill={b} />
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {money(b.totalAmount, b.currency)}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {b.createdAt ? dateFmt.format(new Date(b.createdAt)) : ""}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {/* First: reading the document answers "which site?", and with no delivery address it's the only way. */}
                          {b.status !== "Extracting" && id && (
                            <ViewCompanyBillDocumentButton companyId={id} bill={b} variant="ghost" />
                          )}
                          {b.status === "AwaitingOrganisation" && (
                            <Button size="sm" onClick={() => setAssigning(b)}>
                              Assign
                            </Button>
                          )}
                          {b.status === "ExtractionFailed" && (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={busyBillId === b.id}
                              onClick={() => b.id && handleRetry(b.id)}
                            >
                              {busyBillId === b.id ? (
                                <Spinner className="size-3.5" />
                              ) : (
                                <RefreshCw className="size-3.5" />
                              )}
                              Retry
                            </Button>
                          )}
                          {b.status !== "Extracting" && (
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={busyBillId === b.id}
                              onClick={() => setDiscarding(b)}
                              title="Not this company's bill"
                            >
                              <XCircle className="size-3.5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <CursorPager cursors={billCursors} onNavigate={setBillCursor} className="mt-3" />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="text-base">Organisations</CardTitle>
            <CardDescription>
              The sites in this company. A bill received here can only be assigned to one of these.
            </CardDescription>
          </div>
          <Button size="sm" variant="outline" onClick={() => setLinkOpen(true)}>
            <Plus className="size-3.5" /> Add organisation
          </Button>
        </CardHeader>
        <CardContent>
          {orgError ? (
            <p className="py-6 text-center text-sm text-destructive">{orgError}</p>
          ) : organisations === null ? (
            <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
              <Spinner className="size-3.5" /> Loading organisations...
            </div>
          ) : organisations.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              No organisations yet. Bills received against this company cannot be routed until you add
              at least one.
            </p>
          ) : (
            <div className="overflow-hidden rounded-md border border-border">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/40">
                    <TableHead>Organisation</TableHead>
                    <TableHead>Address</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(organisations ?? []).map((o) => (
                    <TableRow key={o.id}>
                      <TableCell>
                        <Link
                          to={`/organisations/${o.id}`}
                          className="font-medium hover:underline"
                        >
                          {o.name}
                        </Link>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {[o.addressLine1, o.city, o.postalCode].filter(Boolean).join(", ") || "Not set"}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setUnlinking(o)}
                          title="Remove from this company"
                        >
                          <Trash2 className="size-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <CursorPager cursors={orgCursors} onNavigate={setOrgCursor} className="mt-3" />
          <p className="mt-3 text-xs text-muted-foreground">
            Removing an organisation from the company leaves its existing bills untouched. It only stops
            future company bills being routed to it.
          </p>
        </CardContent>
      </Card>

      {id && <CompanyInboundEmailsSection companyId={id} />}

      {id && (
        <>
          <AssignCompanyBillDialog
            companyId={id}
            bill={assigning}
            open={assigning !== null}
            onOpenChange={(open) => !open && setAssigning(null)}
            onAssigned={() => loadBills(id, billCursor)}
          />
          <LinkOrganisationDialog
            companyId={id}
            open={linkOpen}
            onOpenChange={setLinkOpen}
            onLinked={() => loadOrganisations(id, orgCursor)}
          />
        </>
      )}

      {/* The PATCH response is the updated company, so no re-fetch. */}
      <EditCompanyDialog
        company={company}
        open={editOpen}
        onOpenChange={setEditOpen}
        onSaved={setCompany}
      />

      <Dialog open={discarding !== null} onOpenChange={(open) => !open && setDiscarding(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Discard this bill?</DialogTitle>
            <DialogDescription>
              {discarding?.supplierName ?? "This bill"}
              {discarding?.invoiceNumber ? ` (${discarding.invoiceNumber})` : ""} will leave the inbox
              and never reach a ledger. It is kept as a record of why, but it cannot be assigned
              afterwards.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDiscarding(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={busyBillId !== null}
              onClick={() => discarding?.id && handleDiscard(discarding.id)}
            >
              {busyBillId !== null && <Spinner className="size-3.5" />} Discard bill
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={unlinking !== null} onOpenChange={(open) => !open && setUnlinking(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Remove {unlinking?.name} from this company?</DialogTitle>
            <DialogDescription>
              Bills already assigned to it are untouched. From now on no bill received against this
              company can be routed to it, so anything addressed to that site will wait in the inbox
              instead.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUnlinking(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => unlinking?.id && handleUnlink(unlinking.id)}
            >
              Remove organisation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatusCell({ bill }: { bill: CompanyInboxBillResponse }) {
  if (bill.status === "Extracting") {
    return (
      <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
        <Spinner className="size-3" /> Reading the document
      </span>
    );
  }

  if (bill.status === "ExtractionFailed") {
    return (
      <div className="flex items-center gap-1.5">
        <Badge
          variant="outline"
          className="w-fit border-destructive/40 bg-destructive/10 text-destructive"
        >
          Could not be read
        </Badge>
        {bill.extractionError && (
          <InfoHint label="why this could not be read">{bill.extractionError}</InfoHint>
        )}
      </div>
    );
  }

  const outcome = bill.routingOutcome as CompanyRoutingOutcome | null | undefined;
  const copy = outcome ? OUTCOME_LABEL[outcome] : undefined;
  const label = copy?.label ?? "Waiting";

  // suggestedOrganisationConfidence, else the best candidate score (the Ambiguous case nominates no site).
  const best = bill.candidates?.[0];
  const confidence = pct(bill.suggestedOrganisationConfidence ?? best?.score);
  const basis = best?.basis ? BASIS_LABEL[best.basis] ?? best.basis : null;
  const scored = bill.candidates?.length ?? 0;

  // Badge plus an "i": the explanation repeats per outcome, so inline it would double every row's height.
  return (
    <div className="flex items-center gap-1.5">
      <Badge variant="outline" className="w-fit">
        {label}
      </Badge>
      {/* The score the decision turned on: "no confident match" at 71% and at 8% are different problems. */}
      {confidence && (
        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
          {confidence} match
        </span>
      )}
      {(copy?.hint || confidence) && (
        <InfoHint label={label}>
          {copy?.hint}
          {confidence && (
            <>
              {copy?.hint ? " " : ""}
              Best match scored {confidence}
              {basis ? `, on ${basis}` : ""}.{" "}
              {scored > 1
                ? `${scored} organisations were scored against this bill.`
                : "Scores are shown per organisation when you assign it."}
            </>
          )}
        </InfoHint>
      )}
    </div>
  );
}

